import { useId, useLayoutEffect, useRef } from "react";
import type { CSSProperties, PointerEvent, ReactNode } from "react";
import { DieShape } from "./actions.tsx";
import type { ObjectAction } from "./actions.tsx";
import { AnimatedNumber } from "./animated-number.tsx";
import { CardFace, ReviewedText, cardFallback, cardImage, tip } from "./assets.tsx";
import { EquipmentCard } from "./cards.tsx";
import type { DropPayload } from "./drag.tsx";
import { abilityStateClass, abilityStateText } from "./format.ts";
import { GLOSSARY, abilityName, abilityTip, cardCopy, cardEffect, roleCopy } from "./glossary.ts";
import { KleinMemoryCard } from "./klein-memory.tsx";
import { cardAccent } from "./themes.ts";
import { campLabel } from "./types.ts";
import type { CardBrief, Pending, Private, State } from "./types.ts";
import "./player-hud.css";

type DragStart = (event: PointerEvent, payload: DropPayload) => void;


export function PlayerHUD({
  state, priv, me, equipmentPayloads, abilityActions, abilityPayloads, tableActions, pending,
  onRespond, onAction, onDragStart, onInspect, labelOf, children,
}: {
  state: State;
  priv: Private | null;
  me: string;
  equipmentPayloads: Map<string, DropPayload>;
  abilityActions: ReadonlyMap<string, ObjectAction[]>;
  abilityPayloads: ReadonlyMap<string, DropPayload>;
  tableActions: ObjectAction[];
  pending: Pending | null;
  onRespond: (value: unknown) => void;
  onAction: (action: ObjectAction) => void;
  onDragStart: DragStart;
  onInspect: () => void;
  labelOf: (id: string) => string;
  children: ReactNode;
}) {
  const mine = state.players.find((player) => player.id === me);
  if (!mine) return null;
  const role = priv?.role_card;
  const hp = role?.hp ?? mine.hp;
  const roleName = role ? roleCopy(role.id)?.name ?? role.name : "身份未明";
  const memory = priv?.klein_memory ?? [];
  const card = pending?.private_info?.card ?? pending?.context?.card;
  // Only move acceptance out of the prompt when a rendered physical object owns it.
  // Reactive abilities may be offered even though their ordinary active-use flag is false.
  const abilityAcceptance = pending?.kind === "yes_no" && pending.context?.ability
    && priv?.abilities?.some((ability) => ability.id === pending.context?.ability)
    && abilityActions.get(pending.context.ability)?.some(({ action }) =>
      action.type === "respond" && action.prompt_id === pending.prompt_id && action.value === true,
    );
  const choices = pending && !["roll_dice", "choose_place", "choose_place_card", "choose_player", "choose_equipment"].includes(pending.kind)
    ? pending.options.filter((option) => !(pending.kind === "yes_no" && option.value === true && (abilityAcceptance || (card && !pending.context?.ability))))
    : [];
  const canPass = Boolean(pending?.allow_pass && !pending.options.some((option) => option.value === false));
  const extraTurns = state.turn.extra_turn_queue?.filter((player) => player === me).length ?? 0;
  const place = state.board.places.find((entry) => entry.slot === mine.slot);

  return (
    <section className="player-hud" aria-label="自己的角色、符卡与装备">
      <div className="hud-overview">
        <div className="hud-character">
          {children}
          <button type="button" className="hud-role-details portrait-detail-trigger" onClick={onInspect}>角色详情</button>
        </div>
        <div className="hud-state-window">
          <header className="hud-identity">
            <span className="hud-owner">{mine.name} · 御身</span>
            <strong>{roleName}</strong>
            <span>{role ? campLabel(role.camp) : "阵营未明"} · {mine.revealed ? "已公开" : "未公开 · 仅自己可见"}</span>
          </header>
          <dl className="hud-vitals" aria-label="角色体力与已受伤害">
            <div className="hud-health"><dt>剩余体力 HP</dt><dd>{hp === undefined ? "?" : <AnimatedNumber value={Math.max(0, hp - mine.damage)} />}<small> / {hp === undefined ? "?" : <AnimatedNumber value={hp} />}</small></dd></div>
            <div className="hud-damage"><dt>已受伤害</dt><dd><AnimatedNumber value={mine.damage} increaseIsGood={false} /></dd></div>
          </dl>
          <div className="hud-status-tray" aria-label="当前状态">
            <span className="hud-location">{mine.alive ? place?.name ?? "未落位" : "已退场"}</span>
            <span className={`hud-status judge-token${mine.is_judge ? " is-on" : ""}`} {...tip({ title: "審判", summary: GLOSSARY.judge })}>審判 · {mine.is_judge ? "生效中" : "未持有"}</span>
            {extraTurns > 0 && <span className="hud-status">额外回合 ×<AnimatedNumber value={extraTurns} /></span>}
            {state.turn.player === me && state.turn.extra_turns > 0 && <span className="hud-status">剩余额外回合 <AnimatedNumber value={state.turn.extra_turns} /></span>}
          </div>

      <div className="hud-spell-tray" aria-label="符卡能力">
        {(priv?.abilities ?? []).map((ability) => {
          const actions = abilityActions.get(ability.id) ?? [];
          const payload = abilityPayloads.get(ability.id);
          const actionable = actions.length > 0 || Boolean(payload);
          const stateText = abilityStateText(ability, actionable);
          const clickAction = actions.length === 1 && !payload ? actions[0] : undefined;
          return (
            <article
              className={`spell-card ${abilityStateClass(ability, actionable)}${actionable ? " is-targetable" : ""}${payload ? " is-draggable" : ""}`}
              key={ability.id}
              aria-label={`${abilityName(ability.id)} · ${clickAction ? `${clickAction.label} · ` : ""}${stateText}`}
              onPointerDown={payload ? (event) => onDragStart(event, payload) : undefined}
              onClick={clickAction ? () => onAction(clickAction) : undefined}
              role={clickAction ? "button" : undefined}
              tabIndex={clickAction ? 0 : undefined}
              onKeyDown={clickAction ? (event) => {
                if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onAction(clickAction); }
              } : undefined}
              {...tip({ title: abilityName(ability.id), kicker: stateText, summary: abilityTip(ability.id) })}
            >
              <img className="spell-sigil" src="/materials/spell-sigil.webp" alt="" aria-hidden="true" />
              <span className="spell-cast">
                {payload ? <em>拖向目标</em> : actions.length > 1 ? actions.map((action) => (
                  <button type="button" key={`${action.label}-${JSON.stringify(action.action)}`} onClick={(event) => { event.stopPropagation(); onAction(action); }}>{action.label}</button>
                )) : <small>{clickAction?.label ?? abilityName(ability.id).split("「")[0]}</small>}
              </span>
              <span className="hud-spell-state">
                {actionable || ability.usable ? "可用" : "待機"}
                {ability.uses !== undefined ? <> · 已发动 <AnimatedNumber value={ability.uses} /> 次</> : ability.used ? " · 曾发动" : null}
                {(ability.progress !== undefined || ability.id === "marisa_master_spark") && <> · {ability.id === "marisa_master_spark" ? "累计伤害" : "累计值"} <AnimatedNumber value={ability.progress ?? 0} /></>}
              </span>
            </article>
          );
        })}
      </div>
          <div className="hud-card-tray" aria-label="装备与作用记录">
            <div className="hud-equipment-cards" data-equipment-zone={me}>
              {mine.equipment.map((id) => {
                const payload = equipmentPayloads.get(id);
                return <EquipmentCard key={id} id={id} label={labelOf(id)} owner={me} onDragStart={payload ? (event) => onDragStart(event, payload) : undefined} />;
              })}
            </div>
            {mine.attack_immune_until_move && (
              <figure className="hud-record-card is-active" {...tip({ title: labelOf("hakurei_sangetsu_hideout"), kicker: "生效中", summary: "不受攻击伤害，直到本人下次开始移动。", image: cardImage("hakurei_sangetsu_hideout") ?? undefined, color: cardAccent("hakurei_sangetsu_hideout") })}>
                <CardFace id="hakurei_sangetsu_hideout" alt={labelOf("hakurei_sangetsu_hideout")} />
                <figcaption>攻击免疫</figcaption>
              </figure>
            )}
            {memory.map((entry, index) => (
              <KleinMemoryCard
                className="hud-record-card"
                key={`${entry.seq}-${index}`}
                entry={entry}
                players={state.players}
                labelOf={labelOf}
              />
            ))}
          </div>
          {pending?.kind === "choose_equipment" && <div className="hud-equipment-choices" aria-label={pending.text}>
            {pending.options.map((option, index) => (
              <button type="button" className="equipment-choice" key={`${pending.prompt_id}-${index}`} onClick={() => onRespond(option.value)} aria-label={option.label}>
                {typeof option.value === "string" ? <CardFace id={option.value} alt="" /> : option.label}
              </button>
            ))}
          </div>}
          <div className="hud-phase-controls" aria-label="回合操作">
            {tableActions.map((action) => (
              <button
                type="button"
                className={`hud-phase-button is-${action.kind}`}
                data-phase-control
                key={JSON.stringify(action.action)}
                onClick={() => onAction(action)}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {pending && (choices.length > 0 || canPass) && (
        <div className="hud-pending-choices">
          <p>{pending.text}</p>
          <div className="choice-rack" aria-label={pending.text}>
            {choices.map((option, index) => {
              const dice = pending.kind === "choose_dice" && typeof option.value === "object" && option.value !== null
                ? option.value as { die?: string; face?: number }
                : null;
              return (
                <button type="button" className="choice-token" key={`${pending.prompt_id}-${index}`} onClick={() => onRespond(option.value)}>
                  {dice?.die === "d4" || dice?.die === "d6"
                    ? <DieShape kind={dice.die} value={dice.face ?? ""} />
                    : <span className="choice-token-mark" aria-hidden="true">{option.value === false ? "×" : "符"}</span>}
                  <small>{option.label}</small>
                </button>
              );
            })}
            {canPass && <button type="button" className="choice-token is-pass" data-phase-control onClick={() => onRespond(undefined)}><span className="choice-token-mark" aria-hidden="true">×</span><small>不使用</small></button>}
          </div>
        </div>
      )}
    </section>
  );
}

export function HandFan({ me, handCard, handPayload, pending, onRespond, onDragStart, labelOf }: {
  me: string;
  handCard: CardBrief | null;
  handPayload: DropPayload | null;
  pending: Pending | null;
  onRespond: (value: unknown) => void;
  onDragStart: DragStart;
  labelOf: (id: string) => string;
}) {
  const showCardUse = pending?.kind === "yes_no" && !pending.context?.ability && handCard && pending.options.some((option) => option.value === true);
  const reviewed = handCard ? cardCopy(handCard.id) : undefined;
  const detailDialog = useRef<HTMLDialogElement>(null);
  const detailTitle = useId();
  useLayoutEffect(() => {
    detailDialog.current?.close();
  }, [handCard?.id, handCard?.text]);
  return (
    <div className="player-hand-fan hand-arc" data-hand-zone={me} aria-label="自己的手牌">
      {handCard && <figure
        className={`hand-card${handPayload || showCardUse ? " is-playable" : ""}`}
        data-hand-card={handCard.id}
        data-card-owner={me}
        key={handCard.id}
        style={{ "--fan-angle": "-4deg", "--fan-offset": "0px", "--fan-depth": "0px", "--fan-index": 0 } as CSSProperties}
        onPointerDown={handPayload ? (event) => onDragStart(event, handPayload) : undefined}
        onClick={showCardUse ? () => onRespond(true) : undefined}
        role={showCardUse ? "button" : undefined}
        tabIndex={showCardUse ? 0 : undefined}
        onKeyDown={showCardUse ? (event) => {
          if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onRespond(true); }
        } : undefined}
        {...tip({
          title: labelOf(handCard.id),
          kicker: "手牌",
          summary: reviewed ? undefined : cardEffect(handCard.id, handCard.text),
          detail: handPayload?.label ?? (showCardUse ? "点击使用这张卡。" : "当前正在等待这张卡结算。"),
          titleRuns: reviewed?.nameRuns,
          reviewLines: reviewed?.lines,
          image: cardImage(handCard.id) ?? undefined,
          color: cardAccent(handCard.id),
          layout: "card",
        })}
      >
        <img src={cardImage(handCard.id) ?? ""} alt={`${labelOf(handCard.id)}卡面`} onError={cardFallback} draggable={false} />
        <figcaption>{labelOf(handCard.id)}</figcaption>
      </figure>}
      {handCard && <>
        <button
          type="button"
          className="hand-details portrait-detail-trigger"
          aria-haspopup="dialog"
          onClick={() => detailDialog.current?.showModal()}
        >手牌详情</button>
        <dialog
          ref={detailDialog}
          className="hand-detail-dialog"
          aria-labelledby={detailTitle}
          onClick={(event) => { if (event.target === event.currentTarget) event.currentTarget.close(); }}
        >
          <header>
            <h2 id={detailTitle}>{reviewed ? <ReviewedText lines={[reviewed.nameRuns]} /> : labelOf(handCard.id)}</h2>
            <button type="button" autoFocus onClick={() => detailDialog.current?.close()}>关闭</button>
          </header>
          <CardFace id={handCard.id} alt={labelOf(handCard.id)} />
          {reviewed ? <ReviewedText lines={reviewed.lines} className="hand-detail-copy" /> : <p>{cardEffect(handCard.id, handCard.text)}</p>}
        </dialog>
      </>}
    </div>
  );
}
